// BYO log-sink data layer (#448). Reads `GET /v1/billing/log-sink` (non-secret
// coords only — the URL never comes back) and drives the set/clear mutations.
// Mirrors `lib/keys.ts`: round-trip over `apiFetch` reusing the dev session bearer.

import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import { SESSION_TOKEN } from './session';

/** Masked sink status — what `maskLogSink` returns in `apps/api/src/routes/billing.ts`. */
export type LogSinkStatus =
  | { readonly configured: false }
  | {
      readonly configured: true;
      readonly host: string;
      readonly port: number;
      readonly database: string;
      readonly updated_at_iso: string;
    };

export const LOG_SINK_QUERY_KEY = 'log_sink';

/** The caller's sink status. Disabled (idle) when there's no session, like `useKeys`. */
export function useLogSink(token: string | null = SESSION_TOKEN) {
  return createQuery<LogSinkStatus>(() => ({
    queryKey: [LOG_SINK_QUERY_KEY, token, activeAccountId()],
    enabled: token !== null,
    queryFn: async () => apiFetch<LogSinkStatus>('/v1/billing/log-sink', token),
  }));
}

/** Set (or replace) the sink URL. The raw URL is validated + sealed server-side. */
export async function setLogSink(
  url: string,
  token: string | null = SESSION_TOKEN,
): Promise<LogSinkStatus> {
  return apiFetch<LogSinkStatus>('/v1/billing/log-sink', token, {
    method: 'PUT',
    body: JSON.stringify({ url }),
  });
}

/** Clear the sink. Runners stop mirroring at their next enroll refresh. */
export async function clearLogSink(token: string | null = SESSION_TOKEN): Promise<LogSinkStatus> {
  return apiFetch<LogSinkStatus>('/v1/billing/log-sink', token, { method: 'DELETE' });
}
